import type { QueryClient } from '@tanstack/react-query'
import type { Store } from '@tanstack/react-store'

import { isDemoModeActive } from '@/lib/demo-mode'
import { organizationHasFeature } from '@/hooks/use-has-feature'
import type { OrganizationFeatureEnum } from '@/gen/cortado/v1/settings_pb'
import { getOrganizationQueryOptions, organizationStore } from '@/stores/organization.store'
import type { AuthState } from '@/stores/auth.store'

type PrefetchQueryOptions = Parameters<QueryClient['prefetchQuery']>[0]

interface OrganizationRouteContext {
  queryClient: QueryClient
  auth: Store<AuthState>
}

export function canPreloadOrganizationQueries(auth: Store<AuthState>) {
  return (!!auth.state.session || isDemoModeActive()) && !!organizationStore.state.selectedOrganizationId
}

export function preloadQueries(queryClient: QueryClient, queries: readonly unknown[]) {
  for (const queryOptions of queries) {
    void queryClient.prefetchQuery(queryOptions as PrefetchQueryOptions)
  }
}

/**
 * `beforeLoad` gate for a route that needs an organization feature. Throws the
 * redirect `redirectTo` builds when the selected organization lacks `feature`
 * or its grant has expired, so the page never mounts without it.
 *
 * With no session or no selected organization it returns: `_app` owns those
 * redirects. A failed GetOrganization also returns, leaving the page to show
 * its own load error rather than bouncing the user somewhere unrelated.
 */
export async function requireOrganizationFeature(
  { queryClient, auth }: OrganizationRouteContext,
  feature: OrganizationFeatureEnum,
  redirectTo: () => Response,
): Promise<void> {
  const organizationId = organizationStore.state.selectedOrganizationId
  if (!organizationId || !canPreloadOrganizationQueries(auth)) return

  let response
  try {
    response = await queryClient.ensureQueryData(getOrganizationQueryOptions(organizationId))
  } catch {
    return
  }

  if (!organizationHasFeature(response.organization, feature)) throw redirectTo()
}
